import React from "react";
import { Form, Alert, Divider, Space, Button, Tooltip, Typography, Tag, message } from "antd";
import { GlobalOutlined, ReloadOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import { EndpointMappingsComponent } from "../EndpointMappings";
import type { EndpointMapping } from "../../types";
import type { TemplateDynamicFields } from "../../types";
import type { App } from "../../types/api";

const { Text } = Typography;

export interface RoutingTabProps {
  form: FormInstance;
  editingApp: App | undefined;
  isEditMode: boolean;
  editAppId: string | null;
  selectedTemplateId: string | undefined;
  loadTemplateEndpoints: (
    templateId: string,
    showLoader?: boolean,
    appId?: string
  ) => Promise<void>;
  loadingEndpoints: boolean;
  /** Мутація sync ендпоінтів: mutateAsync({ appId, templateId }) повертає { endpoints, syncedEndpoints } */
  syncEndpointsMutation: {
    mutateAsync: (vars: { appId: string; templateId?: string }) => Promise<{ endpoints: EndpointMapping[]; syncedEndpoints: number }>;
    isPending: boolean;
  };
  loading: boolean;
  endpointMappingsKey: number;
  initialMappingsFromDb: EndpointMapping[] | undefined;
  endpointMappingsValue: EndpointMapping[];
  onEndpointMappingsChange: (mappings: EndpointMapping[]) => void;
  /** Після успішної синхронізації ендпоінтів з шаблоном (батько оновить form, config, key). */
  onSyncEndpointsSuccess?: (endpoints: EndpointMapping[]) => void;
  effectiveTemplateDynamicFields: TemplateDynamicFields | null;
  staticSites: Array<{
    id: string;
    siteName?: string;
    template?: { name?: string | null } | null;
    subdomain?: string | null;
    basePath?: string | null;
    domain?: string | null;
    deploymentUrl?: string | null;
    status?: string | null;
  }>;
  dynamicFieldsForEndpoints: Array<{ name: string; value: string }>;
}

export const RoutingTab: React.FC<RoutingTabProps> = ({
  form,
  editingApp,
  isEditMode,
  editAppId,
  selectedTemplateId,
  loadTemplateEndpoints,
  loadingEndpoints,
  syncEndpointsMutation,
  loading,
  endpointMappingsKey,
  initialMappingsFromDb,
  endpointMappingsValue,
  onEndpointMappingsChange,
  onSyncEndpointsSuccess,
  effectiveTemplateDynamicFields,
  staticSites,
  dynamicFieldsForEndpoints,
}) => {
  return (
    <div>
      <Alert
        message="Налаштування маршрутизації"
        description="Тут ви можете налаштувати API endpoint та статичні сайти для вашого додатку"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Divider orientation="left">
        <Space>
          <GlobalOutlined />
          API Ендпоінти
          {selectedTemplateId && !isEditMode && (
            <Tooltip title="Оновити ендпоінти шаблону до останньої версії">
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={async () => {
                  if (selectedTemplateId) {
                    const appIdForEndpoints = editingApp?.id;
                    await loadTemplateEndpoints(
                      selectedTemplateId,
                      true,
                      appIdForEndpoints
                    );
                    message.success("Ендпоінти шаблону оновлено");
                  }
                }}
                loading={loadingEndpoints}
              >
                Оновити ендпоінти
              </Button>
            </Tooltip>
          )}
          {isEditMode && editingApp && (
            <Tooltip title="Синхронізувати ендпоінти з шаблоном та зберегти налаштування з БД">
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={async () => {
                  const formTemplateId = form.getFieldValue("templateId");
                  try {
                    const response = await syncEndpointsMutation.mutateAsync({
                      appId: editingApp.id,
                      templateId: formTemplateId,
                    });
                    message.success(
                      `Синхронізовано ${response.syncedEndpoints} ендпоінтів`
                    );
                    onSyncEndpointsSuccess?.(response.endpoints);
                  } catch {
                    message.error("Помилка синхронізації ендпоінтів");
                  }
                }}
                loading={loading || syncEndpointsMutation.isPending}
              >
                Синхронізувати з шаблоном
              </Button>
            </Tooltip>
          )}
        </Space>
      </Divider>

      <Form.Item name="endpointMappings" style={{ marginBottom: 0 }}>
        <EndpointMappingsComponent
          key={`create-${editAppId ?? "new"}-${endpointMappingsKey}`}
          templateDynamicFields={effectiveTemplateDynamicFields || undefined}
          initialMappingsFromDb={initialMappingsFromDb}
          value={endpointMappingsValue}
          onChange={(mappings) => {
            form.setFieldsValue({ endpointMappings: mappings });
            onEndpointMappingsChange(mappings);
          }}
          staticSites={staticSites.map((s) => ({ ...s, siteName: s.siteName ?? "" }))}
          isEditMode={isEditMode}
          dynamicFields={dynamicFieldsForEndpoints}
          form={form}
          editingApp={editingApp}
        />
      </Form.Item>

      <div>
        <div style={{ marginBottom: "12px" }}>
          <Text type="secondary" style={{ fontSize: "13px" }}>
            Системні макроси (використовуються в налаштуваннях відповідей):
          </Text>
          <div style={{ marginTop: "4px" }}>
            {["OFFER_URL", "OFFER_ERROR"].map((macro) => (
              <Tag key={macro} color="orange" style={{ marginBottom: "4px" }}>
                {`{{${macro}}}`}
              </Tag>
            ))}
          </div>
        </div>

        {effectiveTemplateDynamicFields?.dynamicFields &&
          effectiveTemplateDynamicFields.dynamicFields.length > 0 && (
            <div>
              <Text type="secondary" style={{ fontSize: "13px" }}>
                Макроси з обраного шаблону:
              </Text>
              <div style={{ marginTop: "4px" }}>
                {effectiveTemplateDynamicFields.dynamicFields.map((field) => (
                  <Tag
                    key={field.name}
                    color="blue"
                    style={{ marginBottom: "4px" }}
                  >
                    {`{{${field.name}}}`}
                  </Tag>
                ))}
              </div>
            </div>
          )}

        <div style={{ marginTop: "12px" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            💡 Використовуйте ці макроси в URL полях та налаштуваннях відповідей
          </Text>
        </div>
      </div>
    </div>
  );
};
